import React, { useEffect, useState } from 'react';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { Card, Col, Container, Row, Spinner } from 'react-bootstrap-5';

import ProfileInformation from './steps/profile_information';
import CountySelect from './steps/county_select';
import StepProgress from './step_progress';
import {
  fetchProfile,
  firstSearchUrl,
  subscriptionStatus,
  updateProfile,
  updateProspectAccount
} from '../../helpers/prospect_api';
import { pick } from 'lodash';
import type { Profile } from 'bundles/connect/types';

type Props = {
  brokerSpecialtyOptions: { [key: string]: string };
  counties: { [key: string]: string[]};
  existingProfile: Profile;
  investorTypeOptions: { [key: string]: string };
  otherIndustryOptions: { [key: string]: string };
}

export default function Completion({
  brokerSpecialtyOptions,
  counties,
  investorTypeOptions,
  otherIndustryOptions
}: Props) {
  const [profile, setProfile] = useState<Profile>();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [profileReady, setProfileReady] = useState(false);
  const hasRegions = profile?.regions && profile.regions.length > 0;

  const onRegionChange = (id: number, key: string, val: string) => {
    setProfile((prevProfile) => {
      if (!prevProfile) return prevProfile;
      const regions = prevProfile.regions?.map(region => region.id === id ? { ...region, [key]: val } : region) ?? [];

      return { ...prevProfile, regions };
    });
  };

  const onValueChange = (key: string, val: string) => {
    setProfile((prevProfile) => {
      if (!prevProfile) return prevProfile;
      return { ...prevProfile, [key]: val };
    });
  };

  const onSave = () => {
    setSaving(true);

    const params = pick(profile,
      [
        'companyIsSelf',
        'companyName',
        'countryId',
        'city',
        'state',
        'investorTypes',
        'brokerSpecialties',
        'otherIndustry'
      ]
    );

    updateProfile({ ...params }, 'profile_completion')
      .then(() => {
        return updateProspectAccount({ regions_attributes: profile?.regions });
      })
      .then(() => window.location.href = firstSearchUrl);
  };

  const onNextStep = () => {
    if (step < totalSteps) {
      nextStep();
    } else {
      onSave();
    }
  };

  useEffect(() => {
    // It may take a few seconds for revenue cat to trigger the webhook that sets up their account.
    // This polls for the status of the profile and will only continue when its ready.
    if (!profileReady) {
      const interval = setInterval(() => {
        subscriptionStatus().then((status) => {
          if (status === 'ready') {
            fetchProfile().then((profile) => {
              setProfile(profile);
              setProfileReady(true);
            });
          }
        });
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [profileReady]);

  const nextStep = () => setStep(step + 1);
  const lastStep = () => setStep(step - 1);

  const totalSteps = hasRegions ? 2 : 1;
  const stepLabels =
    [
      { title: 'Profile Information', subTitle: 'Tell us about yourself' },
      { title: 'County Select', subTitle: 'Select your preferred counties' }
    ];

  const steps = () => {
    if (!profile) return null;

    switch (step) {
    case 1:
      return (
        <ProfileInformation
          brokerSpecialtyOptions={brokerSpecialtyOptions}
          investorTypeOptions={investorTypeOptions}
          otherIndustryOptions={otherIndustryOptions}
          profile={profile}
          saving={saving}
          showSave={totalSteps == 1}
          onSubmit={onNextStep}
          onValueChange={onValueChange}
        />
      );
    case 2:
      return (
        <CountySelect
          counties={counties}
          profile={profile}
          saving={saving}
          onBack={lastStep}
          onSave={onSave}
          onValueChange={onRegionChange}
        />
      );
    default:
      return;
    }
  };
  const stepComponent = steps();
  const leftHeaderText = step === 2 ? 'Location, Location, Location.' : 'Welcome!';
  const leftSubText =
    step === 2
      ? 'Let us know where you plan to focus your prospecting.'
      : 'Get ready to upgrade your prospecting power. First, tell us a little bit about yourself.';

  let rightColumnContent;

  if (profileReady) {
    rightColumnContent = (
      <Card className="shadow">
        <Card.Body>
          {totalSteps > 1 && (
            <StepProgress labels={stepLabels} step={step} total={totalSteps} />
          )}
          <SwitchTransition mode="out-in">
            <CSSTransition
              classNames={{
                enter: 'animate__animated',
                enterActive: 'animate__fadeIn',
                exit: 'animate__animated',
                exitActive: 'animate__fadeOut'
              }}
              key={step}
              timeout={300}
            >
              {stepComponent}
            </CSSTransition>
          </SwitchTransition>
        </Card.Body>
      </Card>
    );
  } else {
    rightColumnContent = (
      <div className="text-center">
        <h6>Just a moment, we're setting up your account.</h6>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Container className="d-flex align-items-center min-vh-100">
      <Row className="justify-content-center align-items-center w-100 p-6 g-6">
        <Col className="text-center text-md-start fs-large" md={12} xl={6}>
          <div>
            <h1 className="text-gradient premium-signup-header fw-bolder d-inline-block">{leftHeaderText}</h1>
          </div>
          {leftSubText}
        </Col>
        <Col md={12} xl={6}>
          <SwitchTransition mode="out-in">
            <CSSTransition
              classNames={{
                enter: 'animate__animated',
                enterActive: 'animate__fadeIn',
                exit: 'animate__animated',
                exitActive: 'animate__zoomOut'
              }}
              key={profileReady ? 'intro' : 'steps'}
              timeout={600}
            >
              <div>
                {rightColumnContent}
              </div>
            </CSSTransition>
          </SwitchTransition>
        </Col>
      </Row>
    </Container>
  );
}
